/*
 * <cart-drawer-recommendations> — "You may also like" inside the cart drawer.
 *
 * Lives inside .drawer__inner, which Dawn re-renders on every cart change, so the
 * element is recreated (with a fresh seed product id) and re-fetches automatically.
 * The recommendations Liquid object is only available via the product recommendations
 * route, so the content is fetched client-side rather than rendered with the drawer.
 */
class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    const seed = this.dataset.productId;
    // No items in cart (or no seed) → nothing to recommend.
    if (!seed) {
      this.hidden = true;
      return;
    }
    // Avoid refetching the same seed if the element reconnects unchanged.
    if (this.dataset.loadedSeed === seed) return;

    this.loadRecommendations(seed);
  }

  loadRecommendations(seed) {
    const url = `${this.dataset.url}&product_id=${seed}&section_id=${this.dataset.sectionId}`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-rec');

        if (fresh && fresh.querySelector('.cart-rec__item')) {
          this.innerHTML = fresh.outerHTML;
          this.hidden = false;
          this.dataset.loadedSeed = seed;
          this.initCarousel();
          this.bindAddButtons();
        } else {
          this.hidden = true;
        }
      })
      .catch(() => {
        this.hidden = true;
      });
  }

  initCarousel() {
    const viewport = this.querySelector('[data-cart-rec-viewport]');
    const prev = this.querySelector('[data-cart-rec-prev]');
    const next = this.querySelector('[data-cart-rec-next]');
    if (!viewport || !prev || !next) return;

    const step = () => {
      const item = viewport.querySelector('.cart-rec__item');
      const gap = parseFloat(getComputedStyle(viewport.querySelector('.cart-rec__track')).columnGap) || 0;
      return item ? item.offsetWidth + gap : viewport.clientWidth;
    };

    prev.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: 'smooth' }));
  }

  bindAddButtons() {
    this.querySelectorAll('[data-cart-rec-add]').forEach((button) => {
      button.addEventListener('click', this.addToCart.bind(this));
    });
  }

  addToCart(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId || button.classList.contains('loading')) return;

    // Request exactly the sections cartDrawer.renderContents() reads back, so the
    // ids line up (it looks up parsedState.sections by these same ids).
    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer && cartDrawer.getSectionsToRender
      ? cartDrawer.getSectionsToRender().map((section) => section.id)
      : [];

    button.classList.add('loading');

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    });

    fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: body,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) return; // error payload (e.g. sold out) — leave drawer as-is
        if (cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents(response);
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      })
      .catch(() => {})
      .finally(() => {
        button.classList.remove('loading');
      });
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
